import type { ReactNode } from 'react'
import React, { createContext, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import type { SelectOption } from '@fourthwall/components'
import { useToastsContext } from '@fourthwall/components'
import type { useFormik } from '@fourthwall/utils/dist/formik'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'
import { useUsersQuery } from '@queries/useUsersQuery'
import { routing } from '@utils/routing'

import type { FormValues, Rules } from '../../hooks/useBadgeForm/types'
import { useBadgeForm } from '../../hooks/useBadgeForm/useBadgeForm'
import { useCreateCustomBadgeMutation } from '../../hooks/useCreateCustomBadgeMutation'
import { getIconPayload, getRulesPayload } from '../../utils'

type Props = { children: ReactNode }

type BadgeCreateContextType = {
  form: ReturnType<typeof useFormik<FormValues>>
  tiers?: NonNullable<ReturnType<typeof useTiersQuery>['tiersQuery']['data']>['tiers']
  availableRules: SelectOption[]
  hasEmptyRule: boolean
  hasAnyRules: boolean
  hasAvailableRules: boolean
  usersQuery: ReturnType<typeof useUsersQuery>['usersQuery']
  isLoading: boolean
  getRuleComponent: (rule: Rules[number]) => React.JSX.Element
}

export const BadgeCreateContext = createContext<BadgeCreateContextType | undefined>(undefined)

export const BadgeCreateProvider = ({ children }: Props) => {
  const navigate = useNavigate()
  const toast = useToastsContext()

  const { tiersQuery } = useTiersQuery()

  const { createCustomBadgeMutation } = useCreateCustomBadgeMutation()

  const { usersQuery } = useUsersQuery()

  const { form, availableRules, hasAnyRules, hasAvailableRules, hasEmptyRule, getRuleComponent } =
    useBadgeForm({
      onSubmit: (values) => {
        createCustomBadgeMutation.mutate(
          [
            {
              text: values.text,
              icon: getIconPayload(values.emoji),
              borderColor: values.borderColor,
              backgroundColor: values.backgroundColor,
              textColor: values.textColor,
              rules: getRulesPayload(values),
              minimal: values.badgeType === 'IconOnly',
              usernameColor: values.useCustomUsernameColor ? values.usernameTextColor : undefined,
              removeWhenRulesNoLongerMet: !!values.removeWhenRulesNoLongerMet,
              manuallyAssignedSupporterIds: values.giveToSelectedMembers
                ? values.manuallyAssignedSupporters.map((supporter) => supporter.id)
                : undefined,
              // @ts-ignore
              creatorApplicationSettings: values.giveToSelectedTeamStaff
                ? {
                    type: 'ApplyToSelectedEmails',
                    emails: values.manuallyAssignedTeamStaff,
                  }
                : {
                    type: 'NoSettings',
                  },
            },
          ],
          {
            onSuccess: () => {
              navigate(routing.memberships.settings.badges.self)
              toast.notify(
                'Badge has been created successfully! It may take up to 2 minutes to reflect changes',
                { type: 'success' },
              )
            },
          },
        )
      },
    })

  const value = useMemo(
    () => ({
      form,
      tiers: tiersQuery.data?.tiers,
      availableRules,
      hasEmptyRule,
      hasAnyRules,
      hasAvailableRules,
      usersQuery,
      isLoading: createCustomBadgeMutation.isLoading,
      getRuleComponent,
    }),
    [
      form,
      tiersQuery.data?.tiers,
      availableRules,
      hasEmptyRule,
      hasAnyRules,
      hasAvailableRules,
      usersQuery,
      createCustomBadgeMutation.isLoading,
      getRuleComponent,
    ],
  )

  return <BadgeCreateContext.Provider value={value}>{children}</BadgeCreateContext.Provider>
}
